import { useEffect, useRef, useState } from 'react';
import { FileRejection, useDropzone as useReactDropzone } from 'react-dropzone';
import { IPreview, IUseDropzone } from './dropzone.types';
import {
  getAcceptFromExtensions,
  getUnexpectedErrorMessage,
  validateFiles,
} from './helpers';

export const useDropzone = ({
  setValue,
  files,
  maxFiles,
  acceptedExtensions,
  disabled = false,
  clearYupErrors,
  yupError,
}: IUseDropzone) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [previews, setPreviews] = useState<IPreview[]>([]);
  const previewsRef = useRef(previews);

  // # Reference to the previews state to ensure the latest state is used in cleanup
  useEffect(() => {
    previewsRef.current = previews;
  }, [previews]);

  // # Cleanup function to revoke object URLs when the component unmounts
  useEffect(() => {
    return () => {
      previewsRef.current.forEach((preview) =>
        URL.revokeObjectURL(preview.url),
      );
    };
  }, []);

  // # Prevent native drag and drop behavior when dropzone is disabled or user misses the dropzone area
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const dropHandler = (e: DragEvent) => {
      e.preventDefault();
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', dropHandler);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', dropHandler);
    };
  }, []);

  // # Update error message with yup error
  useEffect(() => {
    if (yupError?.message) {
      setErrorMessage(yupError.message);
    }
  }, [yupError?.message]);

  const removeFile = (fileName: string): void => {
    if (!previews || previews.length === 0) return;

    const fileToRemoveIndex = files.findIndex((obj) => obj.name === fileName);
    if (fileToRemoveIndex > -1) {
      const newFilesArray = files.filter(
        (_, index) => index !== fileToRemoveIndex,
      );
      setValue('files', newFilesArray);
    }

    const previewToRemoveIndex = previews.findIndex(
      (obj) => obj.name === fileName,
    );
    if (previewToRemoveIndex > -1) {
      URL.revokeObjectURL(previews[previewToRemoveIndex].url);
      setPreviews(
        previews.filter((_, index) => index !== previewToRemoveIndex),
      );
    }
  };

  const clearErrors = () => {
    setErrorMessage(undefined);
    clearYupErrors?.();
  };

  const handleDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    clearErrors();

    const result = await validateFiles({
      files,
      newFiles: acceptedFiles,
      maxFiles,
      acceptedExtensions,
      rejectedFiles,
    });

    if (result.errors.length > 0) {
      setErrorMessage(result.errors[0]);
      return;
    }

    if (rejectedFiles.length > 0) {
      setErrorMessage(getUnexpectedErrorMessage());
      return;
    }

    setPreviews([...previews, ...result.previews]);

    setValue('files', [...(files || []), ...acceptedFiles]);
  };

  const { getRootProps, getInputProps, isFocused } = useReactDropzone({
    disabled,
    accept: getAcceptFromExtensions(acceptedExtensions),
    onDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      void handleDrop(acceptedFiles, rejectedFiles);
    },
  });

  return {
    getRootProps,
    getInputProps,
    isFocused,
    previews,
    removeFile,
    errorMessage,
    clearErrors,
  };
};
